import { useState } from "react";
import { ArrowUpRight, Disc3 } from "lucide-react";
import Seo from "@/components/Seo";
import { Ornament } from "@/components/Ornament";
import { Reveal } from "@/components/Reveal";
import { Lightbox } from "@/components/Lightbox";
import { TrackList } from "@/components/TrackList";
import { ALBUMS, GALLERY, STREAMS, VIDEOS } from "@/data/site";

export default function Media() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <main className="pt-32">
      <Seo
        title="Media — 1001 Nights Orchestra"
        siteName="1001 Nights Orchestra"
        description="Performance videos, photo galleries, albums and sample tracks from 1001 Nights Orchestra."
      />
      <header className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-14">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-amber-400">Watch {"\u25C8"} Listen {"\u25C8"} Feel</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-amber-50 tracking-tight">
            The <span className="italic gold-text">Media</span> Archive
          </h1>
        </Reveal>
      </header>

      <section data-testid="videos-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-20">
        <Reveal>
          <h2 className="font-serif text-2xl sm:text-3xl text-amber-100 mb-8">Watch the Orchestra</h2>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {VIDEOS.map((v, i) => (
            <Reveal key={v.youtubeId} delay={i * 0.1}>
              <figure data-testid={`video-card-${i}`} className="border border-amber-500/15 bg-surface/60">
                <div className="aspect-video">
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${v.youtubeId}`}
                    title={v.title}
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
                <figcaption className="px-5 py-4 flex items-center justify-between gap-4">
                  <span className="font-serif text-lg text-amber-100">{v.title}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 shrink-0">{v.note}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
          <Reveal delay={0.15}>
            <a
              href={STREAMS.youtube}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="video-channel-card"
              className="group flex flex-col justify-between border border-amber-500/15 bg-surface/60 hover:border-amber-400/40 transition-colors h-full min-h-[280px] p-8"
            >
              <p className="font-serif text-2xl sm:text-3xl text-amber-100 leading-snug max-w-sm">
                Decades of performances live on the orchestra's channel.
              </p>
              <span className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-amber-300 group-hover:text-amber-100 transition-colors">
                Visit YouTube Channel <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </span>
            </a>
          </Reveal>
        </div>
      </section>

      <section data-testid="music-section" className="border-y border-amber-500/10 bg-[#0a0f1c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-serif text-2xl sm:text-3xl text-amber-100 mb-2">Sample the Repertoire</h2>
              <p className="text-sm text-slate-500 mb-8">Three recordings from the archive — streaming via SoundCloud, right here.</p>
            </Reveal>
            <Reveal delay={0.1}>
              <TrackList idPrefix="media-track" />
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <h2 className="font-serif text-2xl sm:text-3xl text-amber-100 mb-8">Albums</h2>
              <div className="space-y-4">
                {ALBUMS.map((a, i) => (
                  <div key={a.title} data-testid={`album-card-${i}`} className="flex items-center gap-4 border border-amber-500/15 bg-surface/60 p-4 hover:border-amber-400/40 transition-colors">
                    <span className="w-11 h-11 shrink-0 flex items-center justify-center border border-amber-500/25 text-amber-300">
                      <Disc3 size={18} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-lg text-amber-100 leading-snug">{a.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{a.note}</p>
                    </div>
                    <span className="font-mono text-xs text-slate-500">{a.year}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href={STREAMS.spotify} target="_blank" rel="noopener noreferrer" data-testid="media-stream-spotify" className="inline-flex items-center gap-1.5 text-xs text-amber-200 border border-amber-500/25 hover:border-amber-400/60 px-4 py-2 transition-all">
                  Spotify <ArrowUpRight size={12} />
                </a>
                <a href={STREAMS.apple} target="_blank" rel="noopener noreferrer" data-testid="media-stream-apple" className="inline-flex items-center gap-1.5 text-xs text-amber-200 border border-amber-500/25 hover:border-amber-400/60 px-4 py-2 transition-all">
                  Apple Music <ArrowUpRight size={12} />
                </a>
                <a href={STREAMS.youtube} target="_blank" rel="noopener noreferrer" data-testid="media-stream-youtube" className="inline-flex items-center gap-1.5 text-xs text-amber-200 border border-amber-500/25 hover:border-amber-400/60 px-4 py-2 transition-all">
                  YouTube <ArrowUpRight size={12} />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="gallery-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20">
        <Reveal>
          <h2 className="font-serif text-2xl sm:text-3xl text-amber-100 mb-2">On Stage</h2>
          <p className="text-sm text-slate-500 mb-8">From patio nights to concert halls — moments from the road.</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {GALLERY.map((g, i) => (
            <Reveal key={g.src} delay={i * 0.06}>
              <button
                data-testid={`gallery-item-${i}`}
                onClick={() => setLightbox(i)}
                className="group relative block w-full overflow-hidden border border-amber-500/15"
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute inset-0 bg-gradient-to-t from-[#070A12]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-3 left-4 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-200 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  {g.caption}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
        <Ornament className="mt-16" />
        <p className="mt-8 text-center text-sm text-slate-500">
          Press & booking materials available on request via the{" "}
          <a href="/booking" data-testid="media-epk-link" className="text-amber-300 hover:text-amber-100 underline underline-offset-4 decoration-amber-500/40">
            booking page
          </a>
          .
        </p>
      </section>

      <Lightbox items={GALLERY} index={lightbox} onChange={setLightbox} onClose={() => setLightbox(null)} />
    </main>
  );
}
